import React, { useRef, useState, useEffect } from "react";
import { showToast } from "../../../Helper/Toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { appleAuth } from "../../../firebase";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { ClientIdGoogleLogin } from "../../../Utils/Constants";
import {
  getGoogleLoginEnvironment,
  logGoogleLogin,
  logGoogleLoginError,
  sanitizeApiResponse,
  sanitizeTokenResponse,
  sanitizeUserInfo,
} from "../../../Helper/GoogleLoginDebug";
import {
  createAppleAuthProvider,
  getAppleAuthProfile,
  getAppleSignInErrorMessage,
  getProfessionalPostSocialLoginPath,
  saveProfessionalSession,
  shouldFallbackToAppleRedirect,
} from "../../../Helper/ProfessionalSocialLogin";
import { hitLoginProfessionalApi } from "../../../Redux/Professional/LoginSlice";
import { hitSocialLoginProfessionalApi } from "../../../Redux/Professional/SocialLoginProfessionalSlice";
import {
  hitSendEmailVerificationV2Professional,
  hitVerifyEmailVerificationV2Professional,
} from "../../../Redux/Professional/EmailVerificationV2ProfessionalSlice";
import logo from "../../../assets/img/SimpleRenovate.svg";
import google_icon from "../../../assets/img/devicon_google.svg";
import facebook_icon from "../../../assets/img/facebook_icon.svg";
import iphone_icon from "../../../assets/img/iphone_icon.svg";
import AppleIcon from "@mui/icons-material/Apple";



import Loader from "../../Loader/Loader";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import emailIcon from "../../../assets/img/emailIcon.svg";
import lucide_lock from "../../../assets/img/lucide_lock.svg";

const ProfessionalLogin = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const [verifyOtpButton, setVerifyOtpButton] = useState(false);
  const [inputFieldOtp, setInputFieldOtp] = useState(false);
  const [code, setCode] = useState(Array(6).fill(""));
  const inputsRef = useRef([]);
  const [resendTimer, setResendTimer] = useState(0);


  useEffect(() => {
    localStorage.removeItem("clientData");
    localStorage.removeItem("clientToken");
    localStorage.removeItem("clientId");
    if (
      JSON.parse(localStorage.getItem("professionalData"))?.profileSteps === 4
    ) {
      navigation("/professional/dashboard");
    }
  }, []);

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("email") || "";
    const savedPassword = localStorage.getItem("password") || "";

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    logGoogleLogin("professional-login", "page-mounted", {
      environment: getGoogleLoginEnvironment(ClientIdGoogleLogin),
    });
  }, []);

  const continueAfterLogin = (loginData) => {
    if (rememberMe) {
      localStorage.setItem("email", email);
      localStorage.setItem("password", password);
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }

    const d = loginData;
    if (d?.profileSteps == 4) {
      navigation("/professional/dashboard");
      window.location.reload();
      handleViewDetails();
    } else if (d?.profileSteps == 1) {
      navigation("/professional/dashboard");
      window.location.reload();
      handleViewDetails();
    } else if (d?.profileSteps == 0) {
      navigation("/professional/signup");
      handleViewDetails();    
    } else {
      navigation("/professional/signup");
      handleViewDetails();
    }
  };

  const handleViewDetails = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resendEmailVerificationCode = async (e) => {
    e.preventDefault();
    if (resendTimer > 0) return;
    if (!email?.trim()) {
      showToast("Email is required to resend OTP", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await dispatch(
        hitSendEmailVerificationV2Professional({ email: email.trim() }),
      );
      setLoading(false);
      if (response.payload?.status === true) {
        setCode(Array(6).fill(""));
        setOtp("");
        setResendTimer(30);
        showToast(response.payload.message || "Verification code sent", "success");
      } else {
        showToast(response.payload?.message || "Failed to resend code", "error");
      }
    } catch (error) {
      setLoading(false);
      showToast(error?.message || "Failed to resend code", "error");
    }
  };

  const handleOtpCodeChange = (e, index) => {
    const value = e.target.value;
    if (/^[0-9]?$/.test(value)) {
      const next = [...code];
      next[index] = value;
      setCode(next);
      const nextOtp = next.join("");
      setOtp(nextOtp);

      if (value && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // We wrap the button in a component to access the useGoogleLogin hook
  const CustomButton = () => {
    const loginG = useGoogleLogin({
      onSuccess: (tokenResponse) => {
        logGoogleLogin("professional-login", "oauth-token-received", {
          token: sanitizeTokenResponse(tokenResponse),
        });
        handleGoogleLogin(tokenResponse);
      },
      onError: (error) => {
        logGoogleLoginError("professional-login", "oauth-token-request", error, {
          environment: getGoogleLoginEnvironment(ClientIdGoogleLogin),
        });
      },
      prompt: "select_account",
    });

    return (
      <div
        className="google_btn_"
        onClick={() => {
          logGoogleLogin("professional-login", "button-clicked", {
            environment: getGoogleLoginEnvironment(ClientIdGoogleLogin),
          });
          try {
            loginG();
            logGoogleLogin("professional-login", "oauth-flow-started");
          } catch (error) {
            logGoogleLoginError("professional-login", "oauth-flow-start", error);
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          cursor: "pointer",
          width: "100%",
          height: "50px",
          border: "1px solid #ddd",
          borderRadius: "4px",
          backgroundColor: "#fff",
          padding: "0 15px",
        }}
      >
        <img src={google_icon} alt="google icon" style={{ width: 20, height: 20 }} />
        <p
          style={{
            margin: 0,
            color: "#555",
            fontWeight: 500,
            fontSize: 18,
            whiteSpace: "nowrap",
          }}
        >
          Continue with Google
        </p>
      </div>
    );
  };

  const handleGoogleLogin = async (tokenResponse) => {
    logGoogleLogin("professional-login", "handle-login-started", {
      token: sanitizeTokenResponse(tokenResponse),
    });

    if (!tokenResponse?.access_token) {
      logGoogleLoginError(
        "professional-login",
        "missing-access-token",
        new Error("No access_token in Google token response"),
        { token: sanitizeTokenResponse(tokenResponse) },
      );
      showToast("Google login failed: no access token", "error");
      return;
    }

    try {
      logGoogleLogin("professional-login", "userinfo-fetch-started");
      const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });
      logGoogleLogin("professional-login", "userinfo-fetch-response", {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
      });

      const details = await res.json();
      logGoogleLogin("professional-login", "userinfo-parsed", {
        user: sanitizeUserInfo(details),
        hasError: Boolean(details?.error),
        errorMessage: details?.error?.message || details?.error || null,
      });

      if (!res.ok || !details?.sub || !details?.email) {
        logGoogleLoginError(
          "professional-login",
          "userinfo-invalid",
          new Error("Google userinfo missing required fields"),
          {
            ok: res.ok,
            status: res.status,
            user: sanitizeUserInfo(details),
          },
        );
        showToast("Could not get Google account details", "error");
        return;
      }

      logGoogleLogin("professional-login", "social-api-request-started", {
        user: sanitizeUserInfo(details),
      });
      let response = await dispatch(
        hitSocialLoginProfessionalApi({
          type: "Google",
          email: details.email,
          googleId: details.sub,
          profileImage: details.picture,
          firstName: details.given_name,
          lastName: details.family_name,
          isSignupFromMobileOrWeb: 2,
        }),
      );
      logGoogleLogin("professional-login", "social-api-response", {
        api: sanitizeApiResponse(response?.payload),
      });

      if (response.payload.status === true) {
        setLoading(false);

        localStorage.setItem(
          "professionalToken",
          response.payload.data.accessToken,
        );
        localStorage.setItem("professionalId", response.payload.data._id);
        localStorage.setItem(
          "professionalData",
          JSON.stringify(response.payload.data),
        );

        const profileSteps = response.payload?.data?.profileSteps;
        const nextRoute =
          profileSteps == 4 || profileSteps == 1
            ? "/professional/dashboard"
            : "/professional/signup";
        logGoogleLogin("professional-login", "login-success", {
          api: sanitizeApiResponse(response?.payload),
          nextRoute,
        });
        showToast("Login Successful", "success");
        if (profileSteps == 4) {
          navigation("/professional/dashboard");
          window.location.reload();
        } else if (profileSteps == 1) {
          navigation("/professional/dashboard");
          window.location.reload();
        } else if (profileSteps == 0) {
          navigation("/professional/signup");
          window.location.reload();
        } else {
          navigation("/professional/signup");
          window.location.reload();
        }
      } else {
        setLoading(false);
        logGoogleLoginError(
          "professional-login",
          "social-api-rejected",
          new Error(response?.payload?.message || "Social login API failed"),
          { api: sanitizeApiResponse(response?.payload) },
        );
        showToast(response.payload.message, "error");
      }
    } catch (err) {
      logGoogleLoginError("professional-login", "handle-login-unhandled", err);
      showToast("Google login failed. Please try again.", "error");
    }
  };

  const processAppleFirebaseResult = async (result) => {
    const profile = getAppleAuthProfile(result);
    if (!profile?.appleId) {
      showToast("Could not get Apple account details", "error");
      return;
    }
    if (!profile.email) {
      showToast(
        "Apple did not share an email. Use email login or update Apple account permissions.",
        "error",
      );
      return;
    }

    const apiResult = await dispatch(
      hitSocialLoginProfessionalApi({
        type: "Apple",
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        appleId: profile.appleId,
        isSignupFromMobileOrWeb: 2,
      }),
    ).unwrap();

    if (apiResult?.status === true) {
      saveProfessionalSession(apiResult.data);
      showToast("Login Successful", "success");
      navigation(getProfessionalPostSocialLoginPath(apiResult.data?.profileSteps));
      window.location.reload();
      handleViewDetails();
    } else {
      showToast(apiResult?.message || "Apple login failed", "error");
    }
  };

  useEffect(() => {
    let cancelled = false;

    const finishAppleRedirect = async () => {
      try {
        const result = await getRedirectResult(appleAuth);
        if (!result || cancelled) return;
        setLoading(true);
        await processAppleFirebaseResult(result);
      } catch (error) {
        if (!cancelled && error?.code !== "auth/popup-closed-by-user") {
          showToast(getAppleSignInErrorMessage(error), "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    finishAppleRedirect();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAppleLogin = async () => {
    setLoading(true);
    const provider = createAppleAuthProvider();

    try {
      const result = await signInWithPopup(appleAuth, provider);
      await processAppleFirebaseResult(result);
    } catch (error) {
      console.error("Error during Apple Sign-in:", error.code, error.message);
      if (shouldFallbackToAppleRedirect(error)) {
        try {
          await signInWithRedirect(appleAuth, provider);
          return;
        } catch (redirectError) {
          showToast(getAppleSignInErrorMessage(redirectError), "error");
        }
      } else if (error?.code !== "auth/popup-closed-by-user") {
        showToast(getAppleSignInErrorMessage(error), "error");
      }
    } finally {
      setLoading(false);
    }
  };


  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // simple regex
    return regex.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!validateEmail(email)) {
        showToast("Please enter a valid email address.", "error");
        setLoading(false);
        return;
      }
      const res = await dispatch(hitLoginProfessionalApi({ email, password }));
      if (hitLoginProfessionalApi.rejected.match(res)) {
        setLoading(false);
        return;
      }
      if (res.payload?.status === true) {
        const loginData = res.payload?.data || {};
        localStorage.setItem("professionalToken", loginData.accessToken);
        localStorage.setItem("professionalId", loginData._id);
        localStorage.setItem("professionalData", JSON.stringify(loginData));

        if (Number(loginData?.isEmailVerified) === 0) {
          setPendingLoginData(loginData);
          setVerifyOtpButton(true);
          setInputFieldOtp(true);
          setOtp("");
          setCode(Array(6).fill(""));
          const sendOtpRes = await dispatch(
            hitSendEmailVerificationV2Professional({ email: email.trim() }),
          );
          setLoading(false);
          if (sendOtpRes.payload?.status === true) {
            setResendTimer(30);
            showToast(
              "Login successful. Please enter OTP sent to your email.",
              "success",
            );
          } else {
            showToast("Please verify your email with OTP.", "error");
          }
          return;
        }

        setLoading(false);
        showToast("Login Successful", "success");
        continueAfterLogin(loginData);
      } else {
        setLoading(false);
        showToast(res.payload?.message, "error");
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await dispatch(
        hitVerifyEmailVerificationV2Professional({
          email: email.trim(),
          otp: otp,
        }),
      );
      if (res.payload?.status === true) {
        setLoading(false);
        setVerifyOtpButton(false);
        setInputFieldOtp(false);
        setOtp("");
        setCode(Array(6).fill(""));
        showToast("Email verified successfully", "success");
        const verifiedData = res.payload?.data || {};
        const updatedLoginData = {
          ...(pendingLoginData || {}),
          ...verifiedData,
          isEmailVerified: 1,
        };

        if (verifiedData?.accessToken) {
          localStorage.setItem("professionalToken", verifiedData.accessToken);
        }
        if (verifiedData?._id) {
          localStorage.setItem("professionalId", verifiedData._id);
        }
        localStorage.setItem("professionalData", JSON.stringify(updatedLoginData));

        continueAfterLogin(updatedLoginData);
      } else {
        setLoading(false);
        showToast(res.payload?.message || "Invalid verification code", "error");
      }
    } catch (err) {
      setLoading(false);
      showToast("An error occurred. Please try again later.", "error");
    }
  };


  if (loading) {
    return (
      <div style={{ height: "100vh" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="login-form">
      {/* Logo */}
      <div className="login-form__logo mb-3">
        <img src={logo} alt="Logo" />
      </div>

      <p className="login-form__subtitle">Please sign in to continue</p>

      {/* Sign Up Button */}

      <div className="login-form__card">
        {/* <button className="login-form__signup">Sign up as Professional</button> */}

        <h4>Sign In</h4>
        <p>Login with your account by using the form below</p>

        <form onSubmit={verifyOtpButton ? handleVerifyOtp : handleSubmit}>
          <label>
            Email Address <span style={{ color: "#cc4645" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <img
              src={emailIcon}
              alt="email-icon"
              style={{
                position: "absolute",
                left: 10,
                top: 14,
                width: 20,
                height: 20,
              }}
            />
            <input
              style={{ paddingLeft: 40, width: "100%" }}
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <label>
            Password <span style={{ color: "#cc4645" }}>*</span>
          </label>
          <div className="password-input" style={{ position: "relative" }}>
            <img
              src={lucide_lock}
              alt="email-icon"
              style={{
                position: "absolute",
                left: 10,
                top: 11,
                width: 20,
                height: 20,
              }}
            />
            <input
              style={{ paddingLeft: 40, paddingRight: 40, width: "100%" }}
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />{" "}
            <div
              style={{
                position: "absolute",
                right: 10,
                top: 10,
              }}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <VisibilityOutlinedIcon style={{ color: "#9ba3af" }} />
              ) : (
                <VisibilityOffOutlinedIcon style={{ color: "#9ba3af" }} />
              )}
            </div>
          </div>

          <div className="login-form__options">
            <label>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />{" "}
              Remember me
            </label>
            <a href="/professional/forgot-password" className="forgot">
              Forgot Password?
            </a>
          </div>

          {(inputFieldOtp || verifyOtpButton) && (
            <div className="login-otp-block">
              {inputFieldOtp && (
                <div className="info-box" style={{ marginBottom: 12 }}>
                  We&apos;ve sent a verification code to <b>{email}</b>
                </div>
              )}
              {inputFieldOtp && (
                <>
                  <h5 className="my-3" style={{ fontWeight: 700 }}>
                    Enter Verification Code
                  </h5>
                  <div className="code-inputs mb-2 code-inputs-otp">
                    {code.map((digit, idx) => (
                      <input
                        key={idx}
                        type="text"
                        maxLength="1"
                        value={digit}
                        ref={(el) => (inputsRef.current[idx] = el)}
                        onChange={(e) => handleOtpCodeChange(e, idx)}
                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                      />
                    ))}
                  </div>
                </>
              )}

              {verifyOtpButton && (
                <button
                  type="submit"
                  className="verify-btn mb-2"
                  disabled={otp.length < 6}
                  style={{
                    width: "100%",
                    background: otp.length >= 6 ? "#b22222" : "#b3b3b3",
                    cursor: otp.length >= 6 ? "pointer" : "not-allowed",
                  }}
                >
                  Verify Email
                </button>
              )}
              {inputFieldOtp && (
                <p className="login-text mb-3">
                  Didn&apos;t receive code?{" "}
                  <button
                    type="button"
                    className="resend-btn"
                    onClick={resendEmailVerificationCode}
                    disabled={resendTimer > 0}
                    style={{
                      background: "none",
                      border: "none",
                      color: resendTimer > 0 ? "#aaa" : "#b22222",
                      textDecoration: "underline",
                      cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                      padding: 0,
                      font: "inherit",
                      fontWeight: 500,
                    }}
                  >
                    Resend Code{resendTimer > 0 ? ` (${resendTimer}s)` : ""}
                  </button>
                </p>
              )}
            </div>
          )}

          {!verifyOtpButton && (
            <button type="submit" className="login-btn">
              Login
            </button>
          )}

        </form>

        <div className="login-form__divider">Or sign in with</div>

        <div className="social-login social-login-signup">


          <GoogleOAuthProvider clientId={ClientIdGoogleLogin}>
            <div style={{ width: "100%", maxWidth: "400px" }}>
              <CustomButton />
            </div>
          </GoogleOAuthProvider>

          <div className="iphone_btn_" onClick={handleAppleLogin}>
            <AppleIcon />
            <p>Sign in with Apple</p>
          </div>



        </div>

        <p className="register-text">
          Don’t have an account?{" "}
          <a href="/professional/signup">Register here</a>
        </p>
      </div>
    </div>
  );
};

export default ProfessionalLogin;
